import { useEffect, useRef } from 'react';
import * as Icons from 'lucide-react';

const amenities = [
  {
    icon: 'Waves',
    title: 'Infinity Pool',
    description: 'Rooftop aquatic luxury'
  },
  {
    icon: 'person',
    title: 'Sky Yoga',
    description: 'Temperature-controlled aquatic paradise'
  },
  {
    icon: 'Dumbbell',
    title: 'Elite Fitness',
    description: 'Personal training & wellness spa'
  },
  {
    icon: 'Wine',
    title: 'Sky Party Lounge',
    description: 'Climate-controlled collection rooms'
  },
  {
    icon: 'BookOpen',
    title: 'Sky Stargazing Deck',
    description: 'Panoramic city views'
  },
  {
    icon: 'Theater',
    title: 'Sky Cinema',
    description: 'Private screening experience'
  }
];

export const AmenitiesShowcase = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const items = entry.target.querySelectorAll('.amenity-card');
            items.forEach((item, index) => {
              setTimeout(() => {
                item.classList.add('animate-in');
              }, index * 100);
            });
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="amenities" ref={sectionRef} className="relative py-32 bg-black overflow-hidden" style={{backgroundImage:"url('../image/top_side_view.jpeg')"}}>
      {/* Dramatic Background Elements */}
      <div className="absolute inset-0 bg-white/5 ">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-gold-primary/10 rounded-full blur-[120px] animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-gold-dark/10 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-20">
          <h2 className="font-display text-6xl md:text-7xl font-bold text-white mb-6">
            <span className="gradient-gold">World-Class</span> Amenities
          </h2>
          <p className="font-garamond text-2xl text-gray-400 italic">Elevated living, redefined luxury</p>
          <div className="w-48 h-1 bg-gradient-to-r from-transparent via-gold-primary to-transparent mx-auto mt-8"></div>
        </div>

        {/* Amenities Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {amenities.map((amenity, index) => {
            const IconComponent = Icons[amenity.icon] || Icons.Star;
            return (
              <div
                key={index}
                className="amenity-card opacity-0 transform translate-y-10 transition-all duration-700"
              >
                <div className="group relative h-full">
                  {/* Card Background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-gray-dark to-black rounded-2xl"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-gold-primary/0 to-gold-primary/0 group-hover:from-gold-primary/10 group-hover:to-gold-dark/10 rounded-2xl transition-all duration-700"></div>
                  
                  {/* Border Glow */}
                  <div className="absolute -inset-[2px] bg-gradient-to-br from-gold-primary/20 via-transparent to-gold-dark/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 blur-sm"></div>
                  
                  {/* Content */}
                  <div className="relative p-10 border border-gold-primary/20 rounded-2xl group-hover:border-gold-primary/50 transition-all duration-700 bg-white/5 backdrop-blur-lg h-full flex flex-col items-center text-center">
                    {/* Icon */}
                    <div className="mb-6">
                      <div className="relative inline-block">
                        <div className="absolute inset-0 bg-gold-primary/20 rounded-full blur-xl group-hover:blur-2xl transition-all duration-700"></div>
                        <div className="relative w-16 h-16 bg-gradient-to-br from-gold-primary to-gold-dark rounded-full flex items-center justify-center group-hover:scale-110 transform transition-transform duration-500">
                          <IconComponent className="w-8 h-8 text-yellow-400" />
                        </div>
                      </div>
                    </div>

                    {/* Text */}
                    <h3 className="font-display text-2xl font-bold text-white mb-3 group-hover:text-gold-primary transition-colors duration-500">
                      {amenity.title}
                    </h3>
                    <p className="font-sans text-gray-400 leading-relaxed">
                      {amenity.description}
                    </p>

                    {/* Decorative Line */}
                    <div className="mt-6 w-0 h-[2px] bg-gradient-to-r from-gold-primary to-transparent group-hover:w-full transition-all duration-700"></div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        .amenity-card.animate-in {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>
    </section>
  );
};
